import React, { useContext } from 'react'

import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import { StatusChangeReasonGroupType } from '~/common/types/graphql'
import { useGetStatusChangeReasonGroups } from '../hooks/statusChangeReasonGroup/useGetStatusChangeReasonGroups'

interface StatusChangeReasonContextType {
  reasonGroups: StatusChangeReasonGroupType[]
  updateAfterImpersonate: (organizationId: string, skip: boolean) => void
}

const StatusChangeReasonContext =
  React.createContext<StatusChangeReasonContextType>({
    reasonGroups: [],
    updateAfterImpersonate: () => {
      return
    },
  })

const useStatusChangeReasonContext = () => {
  const context = useContext(StatusChangeReasonContext)

  if (!context) {
    throw new Error('statusChangeReasonContext was used outside the provider')
  }
  return context
}

const StatusChangeReasonProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useCurrentUserContext()
  const { data, refetch } = useGetStatusChangeReasonGroups(
    currentUser?.organization?.id || '',
    !currentUser?.isAdmin(),
  )

  const groups = data?.statusChangeReasonGroupConnections

  const isGroupsEmpty = () => {
    return !groups || groups.length === 0
  }
  const isReasonsEmpty = () => {
    return (
      !isGroupsEmpty() &&
      groups.filter(
        (reasonGroup: StatusChangeReasonGroupType) =>
          reasonGroup.statusChangeReasons &&
          reasonGroup.statusChangeReasons?.length > 0,
      ).length === 0
    )
  }

  const updateAfterImpersonate = async (
    organizationId: string,
    skip: boolean,
  ) => {
    if (skip) return

    await refetch({
      organizationId: organizationId,
    })
    localStorage.setItem('is-group-reasons-empty', `${isGroupsEmpty()}`)
    localStorage.setItem('is-reasons-empty', `${isReasonsEmpty()}`)
  }

  React.useEffect(() => {
    localStorage.setItem('is-group-reasons-empty', `${isGroupsEmpty()}`)
    localStorage.setItem('is-reasons-empty', `${isReasonsEmpty()}`)
  }, [data])

  return (
    <StatusChangeReasonContext.Provider
      value={{ reasonGroups: groups, updateAfterImpersonate }}
    >
      {children}
    </StatusChangeReasonContext.Provider>
  )
}

export { StatusChangeReasonProvider, useStatusChangeReasonContext }
